import { Highlight } from 'components/Images/Highlights/HighlightTypes'
import {
  Vect2D,
  ZoomAndPanImg,
  ZoomAndPanImgRef,
} from 'components/Images/ZoomAndPanImg'
import { useBomItemDetailsDialog } from 'features/BomItemDetailsDialog/useBomItemDetailsDialog'
import { debounce, isEqual } from 'lodash'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { GetPDFAttachmentUrl } from 'services/APIs/InternalAPI/GetProjectImageUrl'
import {
  PdfFeature,
  PdfPage,
  PdfViewerModel,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { makeStyles } from 'tss-react/mui'
import { Key } from 'utils/keyCodes'
import { EngDrawingCarrouselToolbox } from './EngDrawingCarrouselToolbox'

const useStyles = makeStyles({ name: 'drawings-carrousel' })((theme) => ({
  contentRoot: {
    userSelect: 'none',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    width: '100%',
    height: '100%',
  },
  img: {
    display: 'block',
  },
  imgContainer: {
    height: '100%',
  },
}))

export type EngDrawingCarrouselProps = {
  bomItemPointer: BomItemPointer
  pdfViewModel: PdfViewerModel
  setPdfViewerModel: React.Dispatch<React.SetStateAction<PdfViewerModel>>
  loading: Record<string, boolean>
  onEscapeKeyDown?: () => void
  onChangedImage?: (drawing: PdfPage) => void
  filterByFileName?: string
  disableWheelZoom?: boolean
  onImgClick?: () => void
  disableToolbox?: boolean
  toolboxAdditionalComponents?: React.ReactNode
  initialImageSrc?: string
}

export type EngDrawingsRef = {
  setDrawingByPageId: (attachmentId: string) => void
  getDrawingSrc: () => string | null
  getCurrentPageId: () => string | null
  getCurrentPageNumber: () => number
  highlightDrawing: (
    highlights: Array<{
      pdfPage: PdfPage
      pdfFeature: PdfFeature
      highlight: Highlight
    }>,
  ) => void
  clearHighlights: () => void
  fitToContainer: () => void
  setPosition: (position: Vect2D) => void
  setZoomAndPosition: (zoom: number, position: Vect2D) => void
  getImgWidth: () => number
  getImgHeight: () => number
}

const HORIZONTALLY_SCROLL_THRESHOULD = 80

const EngDrawingCarrouselComponent = (
  props: EngDrawingCarrouselProps,
  ref: React.Ref<EngDrawingsRef>,
) => {
  const { classes } = useStyles()
  const [currentPageIndex, setCurrentPageIndex] = useState(-1)

  const [imgSrc, setImgSrc] = useState<string | null>()

  useEffect(() => {
    // const pageIndex = props.pdfViewModel?.pages.findIndex(
    //   (page) => page.pageId === props.initialImageSrc,
    // )

    // setCurrentPageIndex(pageIndex !== -1 ? pageIndex : 0)

    zoomAndPanImgRef.current?.fitPage()
  }, [])

  useEffect(() => {
    GetPDFAttachmentUrl(props.bomItemPointer, props.initialImageSrc).then(
      (url) => {
        setImgSrc(url)
      },
    )
  }, [props.bomItemPointer, props.initialImageSrc])

  useEffect(() => {
    // should call fitToContainer when the window is resized
    const handleResize = debounce(() => {
      zoomAndPanImgRef.current?.fitPage()
    }, 200)

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const { pdfViewModel } = { ...props }

  const imgContainerRef = useRef<HTMLDivElement>(null)

  const wheelAccum = useRef(0)

  const openDetailsDialog = useBomItemDetailsDialog()

  const handleChangeDrawing = useCallback(
    (newPageIndex: number) => {
      const newPageId = pdfViewModel?.pages[newPageIndex].pageId

      setCurrentPageIndex(newPageIndex)

      openDetailsDialog('drawings', {
        bomItemPointer: props.bomItemPointer,
        pageId: newPageId,
      })
    },
    [openDetailsDialog, pdfViewModel?.pages, props.bomItemPointer],
  )

  const onNextDrawing = useCallback(() => {
    let newIndex = currentPageIndex + 1

    if (newIndex >= pdfViewModel?.pages?.length) {
      newIndex = 0
    }

    handleChangeDrawing(newIndex)
  }, [currentPageIndex, handleChangeDrawing, pdfViewModel?.pages])

  const onPrevDrawing = useCallback(() => {
    let newIndex = currentPageIndex - 1

    if (newIndex < 0) {
      newIndex = pdfViewModel.pages.length - 1
    }

    handleChangeDrawing(newIndex)
  }, [currentPageIndex, handleChangeDrawing, pdfViewModel?.pages?.length])

  useEffect(() => {
    const abortController = new AbortController()

    window.addEventListener(
      'wheel',
      (e) => {
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          // moving vertically
          return
        }

        // moving horizontally
        wheelAccum.current += e.deltaX

        if (wheelAccum.current >= HORIZONTALLY_SCROLL_THRESHOULD) {
          onPrevDrawing()
          wheelAccum.current = 0
        } else if (wheelAccum.current <= -HORIZONTALLY_SCROLL_THRESHOULD) {
          onNextDrawing()
          wheelAccum.current = 0
        }
      },
      { signal: abortController.signal },
    )

    return () => {
      abortController.abort()
    }
  }, [onNextDrawing, onPrevDrawing])

  useImperativeHandle<EngDrawingsRef, EngDrawingsRef>(ref, () => ({
    setDrawingByPageId: (pageId) => {
      const index = pdfViewModel?.pages.findIndex(
        (model) => model.pageId === pageId,
      )

      if (index !== -1) {
        setCurrentPageIndex(index)

        GetPDFAttachmentUrl(
          props.bomItemPointer,
          pdfViewModel.pages[index].image,
        ).then(setImgSrc)
      }
    },
    getDrawingSrc: () => {
      return pdfViewModel?.pages[currentPageIndex || 0].image || null
    },
    getCurrentPageId: () => {
      return pdfViewModel?.pages?.[currentPageIndex || 0]?.pageId || null
    },
    getCurrentPageNumber: () => {
      return (currentPageIndex || 0) + 1
    },
    highlightDrawing: (
      highlights: Array<{
        pdfPage: PdfPage
        pdfFeature: PdfFeature
        highlight: Highlight
      }>,
    ) => {
      zoomAndPanImgRef.current?.highlightArea(highlights)
    },
    clearHighlights: () => {
      zoomAndPanImgRef.current?.clearHighlights()
    },
    fitToContainer: () => {
      zoomAndPanImgRef.current?.fitPage()
    },
    setZoomAndPosition: (zoom, position: Vect2D) => {
      zoomAndPanImgRef.current?.setZoomAndPosition(zoom, position)
    },
    setPosition: (position) => {
      zoomAndPanImgRef.current?.position(position)
    },
    getImgWidth: () => {
      return zoomAndPanImgRef.current?.getImgWidth() || 0
    },
    getImgHeight: () => {
      return zoomAndPanImgRef.current?.getImgHeight() || 0
    },
  }))

  const handleKeyDown: React.KeyboardEventHandler = useCallback(
    (e) => {
      if (e.key === Key.ArrowLeft) {
        onPrevDrawing()
      } else if (e.key === Key.ArrowRight) {
        onNextDrawing()
      } else if (e.key === Key.Escape) {
        props.onEscapeKeyDown?.()
      }
    },
    [onNextDrawing, onPrevDrawing, props],
  )

  const zoomAndPanImgRef = useRef<ZoomAndPanImgRef>(null)

  const imgOptions = useMemo(() => {
    return {
      disableWheelZoom: props.disableWheelZoom,
      paddingTopFix: 0,
    }
  }, [props.disableWheelZoom])

  if (!props.pdfViewModel) {
    return null
  }

  return (
    <div
      className={classes.contentRoot}
      onClick={(e) => {
        e.stopPropagation()
      }}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={handleKeyDown}
    >
      <div style={{ height: '100%', width: '100%' }}>
        {!props.disableToolbox && (
          <EngDrawingCarrouselToolbox
            currentDrawingIndex={currentPageIndex}
            pdfViewModel={pdfViewModel}
            imgControllerRef={zoomAndPanImgRef}
            handleNextClick={onNextDrawing}
            handlePrevClick={onPrevDrawing}
            additionalComponents={props.toolboxAdditionalComponents}
          />
        )}
        <div className={classes.imgContainer} ref={imgContainerRef}>
          <ZoomAndPanImg
            bomItemPointer={props.bomItemPointer}
            ref={zoomAndPanImgRef}
            src={imgSrc}
            alt="eng drawing"
            className={classes.img}
            options={imgOptions}
            onClick={props.onImgClick}
          />
        </div>
        {/* {pdfViewModel?.pages?.length > 1 && (
          <div className={classes.buttonRight} onClick={handleNextClick}>
            <IconButton disableRipple>
              <ArrowForwardOutlined />
            </IconButton>
          </div>
        )} */}
      </div>
    </div>
  )
}

export default React.memo(
  React.forwardRef(EngDrawingCarrouselComponent),
  isEqual,
)
